/**
 * The Space id a Space dashboard's widget calls carry, held until it is KNOWN.
 *
 * The dashboard frame can start reading before the Spaces list has loaded, and a call sent then went out with no
 * Space: the harness could not tell which business the card belongs to and refused the read (or answered it for
 * another rule), so a Space's cards rendered "Could not load …" under the page's error banner (reported
 * 2026-10-24). A call waits here until the list has settled, at most `timeoutMs`, then goes with whatever is
 * known: the id, or nothing when the list failed or the dashboard has no Space (as before).
 *
 * Import-free on purpose, so it is unit-tested without the app's dependency graph.
 */
export interface SpaceIdGate {
  /** Called on every render: whether the Space is settled (no Space to wait for, or the list answered) and its id. */
  update(settled: boolean, spaceId: string | undefined): void;
  /** The Space id once settled, or undefined after `timeoutMs` without settling. */
  get(timeoutMs?: number): Promise<string | undefined>;
}

export function createSpaceIdGate(): SpaceIdGate {
  let settled = false;
  let current: string | undefined;
  let waiters: Array<(id: string | undefined) => void> = [];
  return {
    update(isSettled, spaceId) {
      settled = isSettled;
      current = spaceId;
      if (settled && waiters.length > 0) {
        const ready = waiters;
        waiters = [];
        ready.forEach((resolve) => resolve(current));
      }
    },
    get(timeoutMs = 5000) {
      if (settled) return Promise.resolve(current);
      return new Promise((resolve) => {
        let done = false;
        const finish = (id: string | undefined) => {
          if (done) return;
          done = true;
          resolve(id);
        };
        waiters.push(finish);
        setTimeout(() => finish(current), timeoutMs);
      });
    },
  };
}
